/**
 * API Usage Logger for SFP Wealth CRM
 * Tracks Claude API calls and calculates costs
 */

const PRICING_CONFIG = {
  'claude-haiku-4.5': {
    inputCostPer1M: 0.8,
    outputCostPer1M: 4.0,
  },
  'claude-sonnet-4.6': {
    inputCostPer1M: 3.0,
    outputCostPer1M: 15.0,
  },
  'claude-opus-4.6': {
    inputCostPer1M: 15.0,
    outputCostPer1M: 75.0,
  },
}

export class APIUsageLogger {
  constructor() {
    this.pricingCache = {}
    this.lastPricingFetch = 0
    this.pricingCacheTTL = 3600000 // 1 hour
  }

  calculateCost(inputTokens, outputTokens, inputCostPer1M, outputCostPer1M) {
    const inputCost = (inputTokens / 1_000_000) * inputCostPer1M
    const outputCost = (outputTokens / 1_000_000) * outputCostPer1M
    const totalCost = inputCost + outputCost

    return {
      inputCost: parseFloat(inputCost.toFixed(6)),
      outputCost: parseFloat(outputCost.toFixed(6)),
      totalCost: parseFloat(totalCost.toFixed(6)),
    }
  }

  getPricingConfig() {
    // For now, return default pricing (could be fetched from Supabase later)
    return PRICING_CONFIG
  }

  logAPICall(logData) {
    try {
      const pricingConfig = this.getPricingConfig()
      const modelPricing = pricingConfig[logData.model]

      if (!modelPricing) {
        console.warn(`No pricing found for model: ${logData.model}`)
        return
      }

      const costs = this.calculateCost(
        logData.inputTokens,
        logData.outputTokens,
        modelPricing.inputCostPer1M,
        modelPricing.outputCostPer1M
      )

      // Log to console
      if (logData.status === 'success') {
        console.log(
          `✓ [${logData.featureName}] ${logData.model} | ` +
          `${logData.inputTokens.toLocaleString()} in | ` +
          `${logData.outputTokens.toLocaleString()} out | ` +
          `$${costs.totalCost.toFixed(4)}`
        )
      } else {
        console.warn(
          `✗ [${logData.featureName}] ${logData.status} | ` +
          `${logData.errorMessage || 'Unknown error'}`
        )
      }

      // Store in localStorage
      const logs = JSON.parse(localStorage.getItem('sfp_apiUsageLogs') || '[]')
      logs.push({
        ...logData,
        timestamp: logData.timestamp || new Date().toISOString(),
        totalCost: costs.totalCost,
      })

      // Keep only last 100 logs
      if (logs.length > 100) {
        logs.shift()
      }

      localStorage.setItem('sfp_apiUsageLogs', JSON.stringify(logs))
    } catch (err) {
      console.warn('Failed to log API call:', err)
    }
  }

  getCostEstimate(model, inputTokens, outputTokens) {
    const pricing = this.getPricingConfig()[model]
    if (!pricing) return 0

    const inputCost = (inputTokens / 1_000_000) * pricing.inputCostPer1M
    const outputCost = (outputTokens / 1_000_000) * pricing.outputCostPer1M
    return inputCost + outputCost
  }

  getAllLogs() {
    try {
      return JSON.parse(localStorage.getItem('sfp_apiUsageLogs') || '[]')
    } catch {
      return []
    }
  }

  getLogsByDate(date) {
    const logs = this.getAllLogs()
    const dateStr = date.toISOString().split('T')[0]

    return logs.filter(log => {
      const logDate = log.timestamp ? new Date(log.timestamp).toISOString().split('T')[0] : ''
      return logDate === dateStr
    })
  }

  getTotalCost(logs) {
    return logs.reduce((sum, log) => sum + (log.totalCost || 0), 0)
  }

  clearLogs() {
    localStorage.removeItem('sfp_apiUsageLogs')
    console.log('✓ API logs cleared')
  }
}

// Export singleton instance
export const apiLogger = new APIUsageLogger()
